import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, ArrowRight, Loader2, BookOpen, GitCompare, ShoppingCart, ShieldCheck, Compass, Cpu, Building2, Users, Target } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ------------------------------- About ---------------------------------- */
export const About = () => (
  <div data-testid="page-about">
    <section className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-5"><Building2 className="w-3.5 h-3.5" /> About us</span>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">The independent home of care technology</h1>
        <p className="text-lg text-gray-600 mt-5">care tech compare helps care providers discover, compare and choose the right technology with confidence - and helps great suppliers get found by the people who need them.</p>
      </div>
    </section>

    <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-10">
      <div className="grid md:grid-cols-3 gap-6">
        {[
          { icon: Target, title: 'Our mission', body: 'Make choosing care technology simple, transparent and trustworthy - no jargon, no hidden agendas.' },
          { icon: GitCompare, title: 'What we do', body: 'A standardised directory and side-by-side comparison of care-tech suppliers and products, plus independent verification and buyer guidance.' },
          { icon: Users, title: 'Who we are', body: 'Built by the team behind CareTechGuide, working with providers, suppliers and sector bodies across the UK.' },
        ].map((c) => (
          <div key={c.title} className="border border-[#E5E7EB] rounded-xl p-6 bg-white">
            <c.icon className="w-7 h-7 text-[#00A79D]" />
            <h2 className="font-semibold text-gray-900 mt-3">{c.title}</h2>
            <p className="text-sm text-gray-600 mt-2">{c.body}</p>
          </div>
        ))}
      </div>
      <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-8">
        <h2 className="font-heading text-2xl font-bold text-gray-900">Our story</h2>
        <p className="text-gray-600 mt-3">CareTechGuide began as a simple idea: care providers deserve a clear, honest place to understand the technology that can transform their services. care tech compare takes that further - adding verification, comparison and procurement support so buyers can make decisions quickly and suppliers can showcase what they do best.</p>
        <p className="text-gray-600 mt-3">We're independent. Suppliers can list for free, and verified suppliers earn a badge that signals trust to buyers - never a pay-to-win ranking.</p>
        <div className="flex flex-wrap gap-3 mt-6">
          <Link to="/browse" className="inline-flex items-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors" data-testid="about-browse-cta">Explore the directory <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/contact" className="inline-flex items-center gap-1.5 border border-[#00A79D] text-[#00857D] hover:bg-[#00A79D]/5 text-sm font-semibold px-5 py-2.5 rounded-md transition-colors">Get in touch</Link>
        </div>
      </div>
    </section>
  </div>
);

/* ------------------------------ Contact --------------------------------- */
export const Contact = () => {
  const [f, setF] = useState({ name: '', organisation: '', email: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.email.trim()) { toast.error('Email required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/leads`, {
        email: f.email.trim(), name: f.name.trim() || null, organisation: f.organisation.trim() || null,
        interest: 'contact', source: 'contact', message: f.message.trim() || null,
      });
      setDone(true); toast.success("Thanks - we'll be in touch shortly.");
    } catch { toast.error('Something went wrong. Please try again.'); }
    finally { setBusy(false); }
  };

  const field = 'w-full px-4 py-3 border border-[#E5E7EB] rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40';
  return (
    <div data-testid="page-contact">
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">Contact us</h1>
          <p className="text-lg text-gray-600 mt-4">Questions, feedback or want to work with us? We'd love to hear from you.</p>
        </div>
      </section>
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-2 gap-10">
        <div>
          <h2 className="font-semibold text-gray-900 text-lg">Get in touch</h2>
          <p className="text-sm text-gray-600 mt-2">Email us directly or send a message and we'll reply as soon as we can.</p>
          <a href="mailto:hello@caretechguide.co.uk" className="inline-flex items-center gap-2 text-[#00857D] font-semibold mt-4" data-testid="contact-email"><Mail className="w-4 h-4" /> hello@caretechguide.co.uk</a>
          <div className="mt-8 border-t border-[#E5E7EB] pt-6">
            <p className="text-sm text-gray-600">Are you a supplier? <Link to="/for-suppliers" className="text-[#00857D] font-semibold">List your product</Link> or <Link to="/pricing" className="text-[#00857D] font-semibold">see pricing</Link>.</p>
          </div>
        </div>
        {done ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center self-start" data-testid="contact-success">
            <Mail className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
            <p className="font-semibold text-gray-900">Message sent.</p>
            <p className="text-sm text-gray-600 mt-1">We'll be in touch shortly.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
            <input type="text" placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} data-testid="contact-name" />
            <input type="text" placeholder="Organisation" value={f.organisation} onChange={(e) => setF({ ...f, organisation: e.target.value })} className={field} data-testid="contact-org" />
            <input type="email" required placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={field} data-testid="contact-email-input" />
            <textarea placeholder="How can we help?" rows={4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={field} data-testid="contact-message" />
            <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3 rounded-md transition-colors disabled:opacity-60" data-testid="contact-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Send message
            </button>
          </form>
        )}
      </section>
    </div>
  );
};

/* ---------------------------- Knowledge Hub ----------------------------- */
const RESOURCES = [
  { to: '/how-to-choose', icon: Compass, title: 'How to choose care tech', desc: 'A practical framework for evaluating and selecting the right technology.' },
  { to: '/compare', icon: GitCompare, title: 'Compare platforms', desc: 'Put suppliers side by side across features, categories and accreditations.' },
  { to: '/procurement', icon: ShoppingCart, title: 'Procurement services', desc: 'Guidance and tailored reports across energy, staffing, PPE, IT and more.' },
  { to: '/assurance', icon: ShieldCheck, title: 'Supplier Assurance', desc: 'How independent verification builds trust between buyers and suppliers.' },
  { to: '/advisor', icon: Cpu, title: 'CareTechAdvisor', desc: 'Your companion for navigating the care technology landscape.' },
  { to: '/browse', icon: BookOpen, title: 'The directory', desc: 'Browse every care-tech supplier and product in one standardised place.' },
];

export const KnowledgeHub = () => (
  <div data-testid="page-knowledge-hub">
    <section className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-5"><BookOpen className="w-3.5 h-3.5" /> Knowledge Hub</span>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">Guidance for choosing care technology</h1>
        <p className="text-lg text-gray-600 mt-5 max-w-2xl mx-auto">Independent guides, comparisons and tools to help you make confident decisions about care technology.</p>
      </div>
    </section>
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {RESOURCES.map((r) => (
          <Link key={r.to} to={r.to} className="group border border-[#E5E7EB] rounded-xl p-6 bg-white hover:border-[#00A79D]/50 hover:shadow-sm transition-all" data-testid={`kh-card-${r.to.slice(1)}`}>
            <r.icon className="w-8 h-8 text-[#00A79D]" />
            <h2 className="font-semibold text-gray-900 mt-3 group-hover:text-[#00857D]">{r.title}</h2>
            <p className="text-sm text-gray-600 mt-2">{r.desc}</p>
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#00857D] mt-4">Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></span>
          </Link>
        ))}
      </div>
    </section>
  </div>
);
